import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  PlayCircle,
  Video,
  ListChecks,
} from "lucide-react";
import { ProgressBar } from "@/components/student/CourseCards";
import { apiError, studentApi, useStudentAuth } from "@/lib/student-auth";

const SPEEDS = [0.75, 1, 1.25, 1.5]; // available inside the Drive player's own settings menu

export default function StudentCoursePlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { student, checking } = useStudentAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [openModules, setOpenModules] = useState({});
  const [speed, setSpeed] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!checking && student === false) nav("/student/login");
  }, [checking, student, nav]);

  useEffect(() => {
    if (!student) return;
    studentApi
      .get(`/student/courses/${slug}`)
      .then((r) => {
        setCourse(r.data);
        const first =
          r.data.modules.flatMap((m) => m.sessions).find((s) => !r.data.completed_session_ids.includes(s.id)) ||
          r.data.modules[0]?.sessions[0];
        setActiveId(first?.id || null);
        setOpenModules(Object.fromEntries(r.data.modules.map((m, i) => [m.id, i === 0])));
      })
      .catch((err) => setError(apiError(err?.response?.data?.detail, "Could not load this course")));
  }, [slug, student]);

  const flat = useMemo(
    () =>
      (course?.modules || []).flatMap((m) =>
        m.sessions.map((s) => ({ ...s, moduleId: m.id, moduleTitle: m.title })),
      ),
    [course],
  );
  const activeIdx = flat.findIndex((s) => s.id === activeId);
  const active = activeIdx >= 0 ? flat[activeIdx] : null;
  const completed = course?.completed_session_ids || [];

  useEffect(() => {
    setNotes(localStorage.getItem(`sp-notes-${slug}-${activeId}`) || "");
  }, [slug, activeId]);

  const toggleComplete = async (sessionId, value) => {
    try {
      const { data } = await studentApi.post("/student/progress", {
        course_slug: slug,
        session_id: sessionId,
        completed: value,
      });
      setCourse((c) => ({ ...c, completed_session_ids: data.completed_session_ids, percent: data.percent }));
      if (data.certificate) {
        toast.success("Course complete — your certificate is ready! 🎉");
      }
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not save progress"));
    }
  };

  if (error) {
    return (
      <main data-testid="player-error" className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-[#020617] px-6 text-center">
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
          <Link to="/student/courses" data-testid="player-back-courses" className="mt-4 inline-flex rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-5 py-2.5 text-xs font-semibold">
            Back to my courses
          </Link>
        </div>
      </main>
    );
  }

  if (!course) {
    return (
      <div data-testid="player-loading" className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-[#020617] text-sm text-slate-500">
        Loading course…
      </div>
    );
  }

  return (
    <main data-testid="student-course-player" className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 dark:border-white/5 bg-white/85 dark:bg-[#020617]/85 backdrop-blur">
        <div className="px-5 sm:px-8 py-3.5 flex items-center gap-3">
          <Link to="/student/courses" data-testid="player-back" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0F172A] dark:hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> My courses
          </Link>
          <div className="ml-auto flex items-center gap-3 min-w-0">
            <div className="hidden sm:block w-32">
              <ProgressBar percent={course.percent} />
            </div>
            <span data-testid="player-percent" className="text-xs font-semibold shrink-0">
              {course.percent}% complete
            </span>
          </div>
        </div>
      </header>

      <div className="p-5 sm:p-8 grid lg:grid-cols-[1fr_330px] gap-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
            {active?.moduleTitle}
          </div>
          <h1 data-testid="player-session-title" className="mt-1.5 font-display text-2xl sm:text-3xl font-semibold tracking-tighter">
            {active?.title || course.title}
          </h1>

          <div className="mt-5 rounded-2xl overflow-hidden border border-slate-200/80 dark:border-white/10 bg-black aspect-video">
            {active?.embed_url ? (
              <iframe
                key={active.id}
                data-testid="player-video-frame"
                src={active.embed_url}
                title={active.title}
                allow="autoplay; fullscreen"
                allowFullScreen
                className="h-full w-full"
              />
            ) : (
              <div data-testid="player-no-video" className="h-full w-full grid place-items-center text-center px-6">
                <div>
                  <Video className="h-6 w-6 mx-auto text-slate-500" />
                  <p className="mt-3 text-sm text-slate-400">
                    The video for this session is being uploaded. Everything else in this course is ready.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="player-prev"
              disabled={activeIdx <= 0}
              onClick={() => setActiveId(flat[activeIdx - 1].id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3.5 py-2 text-xs disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Previous
            </button>
            <button
              type="button"
              data-testid="player-next"
              disabled={activeIdx >= flat.length - 1}
              onClick={() => setActiveId(flat[activeIdx + 1].id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3.5 py-2 text-xs disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>

            <span className="ml-auto text-[11px] text-slate-400 hidden sm:inline">
              Playback speed (0.75x–1.5x) is available in the video player's settings menu
            </span>

            <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-white/10 px-3.5 py-2 text-xs cursor-pointer">
              <input
                data-testid="player-mark-complete"
                type="checkbox"
                checked={active ? completed.includes(active.id) : false}
                onChange={(e) => active && toggleComplete(active.id, e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Mark as complete
            </label>
          </div>

          {active?.description && (
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{active.description}</p>
          )}

          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-2">
              My notes
            </div>
            <textarea
              data-testid="player-notes"
              rows={4}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                localStorage.setItem(`sp-notes-${slug}-${activeId}`, e.target.value);
              }}
              placeholder="Jot down your takeaways for this session…"
              className="w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
            />
          </div>
        </div>

        <aside data-testid="player-module-list" className="lg:sticky lg:top-24 h-fit rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden">
          <div className="px-4 py-3.5 border-b border-slate-200/70 dark:border-white/5">
            <div className="font-display text-sm font-semibold tracking-tight flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-[#2563EB]" /> {course.title}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              {course.module_count} modules · {course.total_sessions} sessions
            </div>
          </div>
          <div className="max-h-[65vh] overflow-y-auto divide-y divide-slate-100 dark:divide-white/5">
            {course.modules.map((m, mi) => {
              const open = openModules[m.id];
              const done = m.sessions.filter((s) => completed.includes(s.id)).length;
              return (
                <div key={m.id}>
                  <button
                    type="button"
                    data-testid={`player-module-${mi + 1}`}
                    onClick={() => setOpenModules((o) => ({ ...o, [m.id]: !o[m.id] }))}
                    className="w-full flex items-start gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <span className="mt-0.5 text-[10px] font-bold text-slate-400 w-8 shrink-0">
                      {String(mi + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium leading-snug">{m.title}</span>
                      <span className="block text-[11px] text-slate-400 mt-0.5">
                        {done}/{m.sessions.length} done
                      </span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <ul className="pb-2">
                      {m.sessions.map((s) => {
                        const isDone = completed.includes(s.id);
                        const isActive = s.id === activeId;
                        return (
                          <li key={s.id}>
                            <button
                              type="button"
                              data-testid={`player-session-${s.id}`}
                              onClick={() => setActiveId(s.id)}
                              className={`w-full flex items-center gap-2.5 pl-12 pr-4 py-2 text-left text-[12.5px] transition-colors ${
                                isActive
                                  ? "bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A] font-medium"
                                  : "hover:bg-slate-50 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300"
                              }`}
                            >
                              {isDone ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                              ) : (
                                <PlayCircle className="h-3.5 w-3.5 shrink-0 opacity-60" />
                              )}
                              <span className="min-w-0 truncate">{s.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </main>
  );
}
